/**
 * Language-prefixed URLs.
 *
 * Arabic, the primary market, keeps the unprefixed paths the site has always
 * had (/project/524), so nothing already indexed moves. Every other language
 * lives under its own prefix (/en/project/524, /ru/project/524 …), which makes
 * each language version a real, separately indexable document with its own
 * canonical and a reciprocal hreflang set.
 *
 * The URL is the single source of truth for the language: the router's
 * basename, i18next's detector, the prerenderer and the sitemap all derive it
 * from here. Plain JS with no imports — the browser bundle, the build scripts
 * and the Vercel Functions all load it.
 */

export const LANGS = ['ar', 'en', 'ru', 'zh', 'ur'];
export const DEFAULT_LANG = 'ar';
export const PREFIXED_LANGS = LANGS.filter((l) => l !== DEFAULT_LANG);

export const RTL_LANGS = ['ar', 'ur'];
export const isRtl = (lang) => RTL_LANGS.includes(lang);

/** hreflang / <html lang> values. */
export const HREFLANG = { ar: 'ar', en: 'en', ru: 'ru', zh: 'zh', ur: 'ur' };

export const OG_LOCALE = { ar: 'ar_AE', en: 'en_US', ru: 'ru_RU', zh: 'zh_CN', ur: 'ur_PK' };

/** Native names for the language switcher. */
export const LANG_NAMES = { ar: 'العربية', en: 'English', ru: 'Русский', zh: '中文', ur: 'اردو' };

/**
 * "/en/project/524" → { lang: 'en', path: '/project/524' }
 * "/en"             → { lang: 'en', path: '/' }
 * "/project/524"    → { lang: 'ar', path: '/project/524' }
 */
export function splitLangPath(pathname = '/') {
    const clean = '/' + String(pathname).replace(/^\/+/, '');
    const seg = clean.split('/')[1] || '';
    if (PREFIXED_LANGS.includes(seg)) {
        const rest = clean.slice(seg.length + 1) || '/';
        return { lang: seg, path: rest.startsWith('/') ? rest : `/${rest}` };
    }
    return { lang: DEFAULT_LANG, path: clean };
}

/** "/project/524" + "en" → "/en/project/524"; "/" + "en" → "/en". */
export function localizedPath(path = '/', lang = DEFAULT_LANG) {
    const p = '/' + String(path).replace(/^\/+/, '');
    if (lang === DEFAULT_LANG || !PREFIXED_LANGS.includes(lang)) return p;
    return p === '/' ? `/${lang}` : `/${lang}${p}`;
}

/** The react-router basename for a language: undefined for Arabic. */
export const basenameFor = (lang) => (lang === DEFAULT_LANG ? undefined : `/${lang}`);

/**
 * The hreflang set for one page: every language it exists in, plus
 * x-default. x-default is the Arabic URL when the page has an Arabic version
 * (the default market), otherwise the English one.
 */
export function hreflangSet(site, path, langs = LANGS) {
    const available = LANGS.filter((l) => langs.includes(l));
    const links = available.map((l) => ({ hreflang: HREFLANG[l], lang: l, href: `${site}${localizedPath(path, l)}` }));
    const xDefault = available.includes(DEFAULT_LANG) ? DEFAULT_LANG : available.includes('en') ? 'en' : available[0];
    if (xDefault) links.push({ hreflang: 'x-default', lang: xDefault, href: `${site}${localizedPath(path, xDefault)}` });
    return links;
}

/** Strips the legacy ?lang= parameter: "?lang=en&utm=x" → "?utm=x". */
export function withoutLangParam(search = '') {
    const params = new URLSearchParams(search);
    params.delete('lang');
    const s = params.toString();
    return s ? `?${s}` : '';
}
